import { AxiosResponse } from 'axios'
import { useSignalR } from 'BrokerTsLib/src/context/SignalRProvider'
import { Offer } from 'BrokerTsLib/src/dtos'
import { ReactNode, useEffect, useState } from 'react'
import { QueryObserverResult, UseMutationResult, UseQueryResult } from 'react-query'
import { GenericQueryOptions } from '../hooks'
import { CalculateOffersRequest } from '../hooks/useOffer'

export enum SortBy {
    Price = 'price',
    Insurer = 'insurer',
    VariantPrice = 'variantPrice',
    VariantInsurer = 'variantInsurer'
}

export interface IOffersContext<T extends Offer> {
    sortBy: SortBy
    offers: T[]
    offersIds: number[]
    isLoading: boolean
    calculatingOffersIds: number[]
    showAll: boolean
    setShowAll: (showAll: boolean) => void
    setSortBy: (data: SortBy) => void
    recalculateOffers: (offerIds?: number[], force?: boolean) => Promise<AxiosResponse<T[], any>>
    updateOffer: ((offer: T) => void)
    refetchOffers: () => Promise<QueryObserverResult<AxiosResponse<T[], any>, any>>
}

export const defaults: IOffersContext<Offer> = {
    sortBy: SortBy.Price,
    offers: [],
    offersIds: [],
    isLoading: false,
    calculatingOffersIds: [],
    showAll: true,
    setShowAll: () => { },
    setSortBy: () => { },
    recalculateOffers: () => { return null as any },
    updateOffer: () => { },
    refetchOffers: () => { return null as any }
}

export type BaseOffersProviderProps = {
    insuranceParamsId: string
    isContract: boolean
    children: ReactNode
}

type OffersProviderProps<T extends Offer> = {
    signalRCalcFinishedEventName: string
    useGetOffers(paramsId: string, options?: GenericQueryOptions<T[]>): UseQueryResult<AxiosResponse<T[], any>, any>
    useCalculateOffers(): UseMutationResult<AxiosResponse<T[], any>, unknown, CalculateOffersRequest, unknown>
    context: React.Context<IOffersContext<T>>
} & BaseOffersProviderProps

export function OffersProvider<T extends Offer>({ children, insuranceParamsId, isContract, useGetOffers, useCalculateOffers, context, signalRCalcFinishedEventName }: OffersProviderProps<T>) {
    const [sortBy, setSortBy] = useState<SortBy>(SortBy.Price)
    const [showAll, setShowAll] = useState(false)
    const [calculatingOffersIds, setCalculatingOffersIds] = useState<number[]>([])
    const [offersIds, setOfferIds] = useState<number[]>([])
    const [offers, setOffers] = useState<T[]>([])

    const signalR = useSignalR()
    const hubId = signalR?.connectionId ?? ''

    const { mutateAsync: calculateOffers, isLoading: isCalculating } = useCalculateOffers()
    const { refetch: refetchOffers, isLoading: isLoadingOffers, isFetching: isFetchingOffers } = useGetOffers(insuranceParamsId, {
        enabled: false,
        retry: false,
        cacheTime: 0,
        onSuccess: (({ data }) => {
            setOfferIds(data.map(o => o.offerId) ?? [])

            if (isContract)
                setOffers(data)
        })
    })

    const calculate = (ids?: number[], force: boolean = false) => {
        setCalculatingOffersIds(ids ?? offers.map(x => x.offerId))
        return calculateOffers({ hubId, offerIds: ids, paramsId: insuranceParamsId, forceCalculation: force ?? false })
    }

    const updateOffer = (offer: T) => {
        setOffers(prev => {
            var index = prev.findIndex(x => x.offerId == offer.offerId)
            if (index >= 0) {
                prev[index] = offer
                return [...prev]
            }

            return [...prev, offer]
        })
    }

    // ziskanni / zajisteni vytvoreni offers
    useEffect(() => {
        refetchOffers()
    }, [])

    // ziskani kalkulaci
    useEffect(() => {
        if (!!hubId && offersIds.length > 0 && !isContract)
            calculate(offersIds)
    }, [hubId, offersIds, isContract])

    // kalkulace se vraci postupne pres signalR
    useEffect(() => {
        signalR?.on(signalRCalcFinishedEventName, (offer: T) => {
            setCalculatingOffersIds(prev => [...prev.filter(x => x != offer.offerId)])
            updateOffer(offer)
        })

        return () => {
            signalR?.off(signalRCalcFinishedEventName)
        }
    }, [signalR])

    return (
        <context.Provider value={{
            sortBy: sortBy,
            offers: offers ?? [],
            offersIds: offersIds ?? [],
            isLoading: isLoadingOffers || isFetchingOffers || (offers?.filter(x => x.included)?.length ?? 0) == 0 && isCalculating,
            calculatingOffersIds: calculatingOffersIds,
            showAll: showAll,
            setSortBy: setSortBy,
            recalculateOffers: (offersIds, force) => calculate(offersIds, force),
            updateOffer: updateOffer,
            setShowAll: setShowAll,
            refetchOffers: refetchOffers,
        }}>
            {children}
        </context.Provider>
    )
}

